import {useState} from 'react'
import {
    Employee,
    EmployeeFormData,
} from 'features/authorized/components/portal/components/qr/common/core/entities/employee'
import useAddEmployee from 'features/authorized/components/portal/components/qr/common/hooks/useAddEmployee'
import useUpdateEmployee from 'features/authorized/components/portal/components/qr/common/hooks/useUpdateEmployee'
import {z} from 'zod'
import {phoneRegex} from 'features/common/core/regex'

type NewEmployeeErrors = Partial<Record<keyof EmployeeFormData, string[]>>

const employeeSchema = z.object({
    avatar: z.string().or(z.literal('')),
    firstName: z
        .string()
        .min(2, {message: 'VALIDATION_TOO_SHORT'})
        .max(43, {message: 'VALIDATION_TOO_LONG'}),
    phone: z
        .string()
        .min(4, {message: 'VALIDATION_TOO_SHORT'})
        .max(40, {message: 'VALIDATION_TOO_LONG'})
        .regex(phoneRegex, 'VALIDATION_PHONE_FORMAT')
        .or(z.literal('')),
    email: z
        .string()
        .min(4, {message: 'VALIDATION_TOO_SHORT'})
        .max(40, {message: 'VALIDATION_TOO_LONG'})
        .email({message: 'VALIDATION_INVALID_EMAIL'})
        .or(z.literal('')),
    birth: z.string().datetime().or(z.literal('')),
    role: z.string().or(z.literal('')),
})

const emptyFormData: EmployeeFormData = {
    avatar: '',
    firstName: '',
    birth: null,
    phone: '',
    email: '',
    role: '',
}

const useEmployeeForm = ({
    initialData,
    onSuccess,
}: {
    initialData: Employee | null
    onSuccess: () => void
}) => {
    const initialState = initialData || emptyFormData
    const [formData, setFormData] = useState<EmployeeFormData>(initialState)
    const [errors, setErrors] = useState<NewEmployeeErrors>({})

    const {mutate: addEmployee} = useAddEmployee(onSuccess)
    const {mutate: updateEmployee} = useUpdateEmployee(onSuccess)

    const onChange = ({
        prop,
        value,
    }: {
        prop: keyof EmployeeFormData
        value: EmployeeFormData[keyof EmployeeFormData]
    }) => {
        setFormData((fd) => {
            const newData = {...fd, [prop]: value}
            const parseRes = employeeSchema.safeParse(newData)

            if (!parseRes.success) {
                const newErr = parseRes.error.flatten().fieldErrors[prop]
                setErrors((errs) => {
                    if (newErr) {
                        return {...errs, [prop]: newErr}
                    } else {
                        delete errs[prop]
                        return errs
                    }
                })
            } else {
                setErrors({})
            }

            return newData
        })
    }

    const onSubmit = () => {
        if (initialData) {
            updateEmployee({
                ...initialData,
                ...formData,
            })
            onSuccess()
            return
        }
        addEmployee(formData)
        onSuccess()
    }

    const errorsInForm = !!(
        errors.firstName ||
        errors.email ||
        errors.phone ||
        errors.birth
    )
    const requiredFieldsEmpty = !formData.firstName
    const disableSubmit = errorsInForm || requiredFieldsEmpty

    return {formData, handlers: {onChange, onSubmit}, errors, disableSubmit}
}

export default useEmployeeForm
